import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const responsesAdapter = createEntityAdapter({
    sortComparer: (a, b) => (a.completed === b.completed) ? 0 : a.completed ? 1 : -1
})

const initialState = responsesAdapter.getInitialState()

export const responsesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getResponses: builder.query({
            query: () => ({
                url: '/api/responses',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedResponses = responseData.map(response => {
                    response.id = response._id
                    return response
                });
                return responsesAdapter.setAll(initialState, loadedResponses)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Response', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Response', id }))
                    ]
                } else return [{ type: 'Response', id: 'LIST' }]
            }
        }),
        getUpdatedResponses: builder.query({
            query: ({full}) => ({
                url: `/api/responses/updated?${full?"full="+ full:""}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            providesTags: (result, error, arg) => {
                return [{ type: 'ResponseU', id: 'LIST' }]
            }
        }),
        getGlobalSearch: builder.mutation({
            query: (initialResponse) => ({
                url: `/api/responses/global-search-response`,
                method: 'POST',
                body: {
                    ...initialResponse,
                },
               
            }),
            providesTags: (result, error, arg) => ({
                type: "Record", id: arg
              })
        }),
        getResponseById: builder.query({
            query: ({id}) => ({
                url: `/api/responses/${id}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            providesTags: (result, error, arg) => {
                return [{ type: 'ResponseById', id: arg.id }]
            }
        }),
        getTaskGrades: builder.query({
            query: ({ taskId }) => ({
                url: `/api/responses/grades/${taskId}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            providesTags: (result, error, arg) => {
                  return [{ type: 'Grade', id: 'LIST' }]
            }
        }), 
        addNewResponse: builder.mutation({
            query: initialResponse => ({
                url: '/api/responses',
                method: 'POST',
                body: {
                    ...initialResponse,
                }
            }),
            invalidatesTags: [
                { type: 'Response', id: "LIST" },
                { type: 'Task', id: "LIST" }
            ]
        }),
        updateResponse: builder.mutation({
            query: initialResponse => ({
                url: '/api/responses',
                method: 'PATCH',
                body: {
                    ...initialResponse,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Response', id: arg.id },
                { type: 'ResponseById', id: arg.id },
                { type: 'Task', id: "LIST" }
            ]
        }),
        updateGrade: builder.mutation({
            query: initialResponse => ({
                url: '/api/responses/grade',
                method: 'PATCH',
                body: {
                    ...initialResponse,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Response', id: arg.id },
                { type: 'Grade', id: 'LIST' },
                { type: 'ResponseU', id: 'LIST' },
                { type: 'ResponseById', id: arg.id }
            ]
        }),  
        updateMultiGrade: builder.mutation({
            query: initialResponse => ({
                url: '/api/responses/multi-grade',
                method: 'PATCH',
                body: {
                    ...initialResponse,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Response', id: arg.id },
                { type: 'Grade', id: 'LIST' }
            ]
        }),  
        fillMissingResponse: builder.mutation({
            query: initialResponse => ({
                url: '/api/responses/fill-missing-response',
                method: 'PATCH',
                body: {
                    ...initialResponse,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Response', id: arg.id },
                { type: 'Grade', id: 'LIST' }
            ]
        }),  
        
        
        deleteResponse: builder.mutation({
            query: ({ id }) => ({
                url: `/api/responses`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Response', id: arg.id }
            ]
        }),
    }),
})

export const {
    useGetResponsesQuery,
    useGetTaskGradesQuery,
    useGetUpdatedResponsesQuery,
    useAddNewResponseMutation,
    useUpdateResponseMutation,
    useUpdateGradeMutation,
    useUpdateMultiGradeMutation,
    useDeleteResponseMutation,
    useGetResponseByIdQuery, 
    useFillMissingResponseMutation, 
    useGetGlobalSearchMutation
} = responsesApiSlice

// returns the query result object
export const selectResponsesResult = responsesApiSlice.endpoints.getResponses.select()

// creates memoized selector
const selectResponsesData = createSelector(
    selectResponsesResult,
    responsesResult => responsesResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllResponses,
    selectById: selectResponseById,
    selectIds: selectResponseIds
    // Pass in a selector that returns the responses slice of state
} = responsesAdapter.getSelectors(state => selectResponsesData(state) ?? initialState)